// React
import { useCallback } from 'react';

// Mobx
import { observer } from 'mobx-react';

// Antd
import { Menu, Spin, Typography } from 'antd';
import { MenuUnfoldOutlined, CloseOutlined } from '@ant-design/icons';

// Interfaces
import { ISidebarProps } from './interfaces';

// Styles
import styles from './index.module.scss';
import './styles.scss';

// Components
import { BaseImage, BaseTitle } from 'components';

// i18n
import { useTranslation } from 'react-i18next';

// React Router DOM
import { useParams, useHistory, useLocation } from 'react-router-dom';

// Query String
import qs from 'query-string';

// Constant
import { ASSESSMENT_URL, CERTIFICATION_URL } from 'constant/url.constant';

const Sidebar = observer(
  ({
    competencyUnits,
    isLoading,
    selectedCompetencyUnitId,
    isFromAssessment,
    isSidebarOpen,
    setToggleSidebar,
  }: ISidebarProps) => {
    // Hook
    const location = useLocation();
    const queryString = qs.parse(location.search) as { preparationRevisit?: string };
    const params = useParams<{ certificationId: string; assessmentId?: string }>();
    const history = useHistory();
    const { t } = useTranslation();

    /**
     * @description Redirect to selected unit competency
     *
     * @param {number} competencyUnitId
     *
     * @return {void} void
     */
    const onRedirectCompetencyUnit = useCallback(
      (competencyUnitId: number): void => {
        if (isFromAssessment) {
          history.push(`${ASSESSMENT_URL.INDEX}/${params.assessmentId}/detail/${competencyUnitId}`);
        } else {
          if (queryString.preparationRevisit === 'true') {
            history.push(
              `${CERTIFICATION_URL.REGISTRATION}${params.certificationId}/assessment/${params.assessmentId}/self-assessment/${competencyUnitId}?preparationRevisit=true`,
            );
          } else {
            history.push(
              `${CERTIFICATION_URL.REGISTRATION}${params.certificationId}/assessment/${params.assessmentId}/self-assessment/${competencyUnitId}`,
            );
          }
        }
      },
      [history, isFromAssessment, params.assessmentId, params.certificationId, queryString.preparationRevisit],
    );

    return (
      <Spin spinning={isLoading}>
        <div className={`${styles['sidebar-container']} ${isSidebarOpen ? styles['open'] : ''}`}>
          {/* Toggler - Mobile Only */}
          {!isSidebarOpen ? (
            <MenuUnfoldOutlined
              className={styles['only-mobile']}
              style={{ fontSize: 24 }}
              onClick={() => setToggleSidebar(!isSidebarOpen)}
            />
          ) : (
            <div className={styles['close-sidebar']}>
              <CloseOutlined
                className={styles['only-mobile']}
                style={{ fontSize: 24 }}
                onClick={() => setToggleSidebar(!isSidebarOpen)}
              />
            </div>
          )}

          {isSidebarOpen && (
            <div className={styles['sidebar-credentials']}>
              <div className={'flex flex-col items-start ml-2'}>
                <BaseTitle title={t('shared.assessment.selfAssessment.competencyUnitList')} />
              </div>
            </div>
          )}

          {/* Divider */}
          <div className={styles['sidebar-line']} />

          {/* Menu */}
          <Menu
            defaultSelectedKeys={[selectedCompetencyUnitId]}
            selectedKeys={[selectedCompetencyUnitId]}
            mode="inline"
            theme="dark"
            inlineCollapsed={false}
            className={`layout-back-office-sidebar ${styles['sidebar']} basic mt-2`}
          >
            {competencyUnits.map((result, index) => (
              <Menu.Item key={result.competencyUnitId} onClick={() => onRedirectCompetencyUnit(result.competencyUnitId!)}>
                <div className="flex items-center justify-between">
                  {isSidebarOpen && (
                    <Typography.Paragraph
                      ellipsis={{ tooltip: result.competencyUnitName }}
                      style={{
                        marginBottom: 0,
                        color: result.competencyUnitId === parseInt(selectedCompetencyUnitId) ? 'white' : 'black',
                      }}
                    >
                      {index + 1}. {result.competencyUnitName}{' '}
                    </Typography.Paragraph>
                  )}
                  <BaseImage
                    icon={result.totalCompetency === result.finishedCompetency ? 'check-active' : 'check'}
                    isIcon
                    width={'20px'}
                    height={'20px'}
                  />
                </div>
              </Menu.Item>
            ))}
          </Menu>
        </div>
      </Spin>
    );
  },
);

export { Sidebar };
